import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  input,
  output,
  signal,
  viewChild,
  viewChildren
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';

import { RouterLinkWithHrefDirective } from '@amfam/shared/ui/ds-router-link/lib/router-link.directive';

import {
  MobileLinkClickEvent,
  MobileSublinkClickEvent,
  NavLinkTreeObj
} from '../../../models/links-model';
import { HeaderWrapperService } from '../../../services/header-wrapper.service';

@Component({
  selector: 'ds-header-mobile-navigation-item',
  templateUrl: './ds-header-mobile-navigation-item.component.html',
  styleUrls: [
    '../shared/ds-header-mobile-navigation.scss',
    './ds-header-mobile-navigation-item.component.scss'
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLinkWithHrefDirective, NgClass]
})
export class DsHeaderMobileNavigationItemComponent {
  private headerWrapperService = inject(HeaderWrapperService);
  private router = inject(Router);

  readonly showSub = signal('hide');
  readonly showNavItem = signal('show');
  readonly link = input<NavLinkTreeObj>();

  readonly linkClickEvent = output<MobileLinkClickEvent>();
  readonly linkBlurEvent = output<DsHeaderMobileNavigationItemComponent>();
  readonly sublinkClickEvent = output<MobileSublinkClickEvent>();
  readonly sublinkTitleClickEvent = output<void>();

  readonly subLinkElemItems = viewChildren<ElementRef>('subLinkElemItem');
  readonly sublinkTitle = viewChild<ElementRef>('sublinkTitle');
  // Set the item to focus here, but wait to focus it
  // until the animation is complete, therwise it won't work.
  private readonly previousFocusElem = toSignal(this.headerWrapperService.mobileMenuOpenItem$, {
    initialValue: null
  });

  linkClick(event: Event) {
    event.preventDefault();
    const currentElem = event.currentTarget as HTMLElement;
    this.linkClickEvent.emit({ link: this, event });
    if (!this.link()?.textOnly) {
      this.headerWrapperService.setMobileSublinkActive(currentElem);
    }

    // If the link item has children, open the sub-menu and don't route
    if (this.link()?.subLinkArray) {
      this.showSub.set('show');
      this.headerWrapperService.setMobileMenuOpenItem(currentElem);
      // Else route as there's no sub-menu to open
    } else {
      if (this.link()?.linkRoute) {
        return this.link()?.queryParams
          ? this.router.navigate([this.link()?.linkRoute, this.link()?.queryParams])
          : this.router.navigate([this.link()?.linkRoute]);
      }
    }
  }

  onLinkBlur() {
    this.linkBlurEvent.emit(this);
  }

  sublinkClick(sublink: NavLinkTreeObj, event: Event) {
    event.preventDefault();
    // Bubble up event
    this.sublinkClickEvent.emit({ sublink, event });
    // Close mobile menu unless specified otherwise with the textOnly property
    if (!sublink.textOnly) {
      this.headerWrapperService.setMobileMenuOpen(false);
      this.showSub.set('hide');
    }
  }

  sublinkTitleClick(event: Event) {
    event.preventDefault();
    // Bubble up event
    this.sublinkTitleClickEvent.emit();
    this.showSub.set('hide');
  }

  private focusSublinkTitle() {
    this.sublinkTitle()?.nativeElement?.focus();
  }

  private focusPreviousElem() {
    this.previousFocusElem()?.focus();
  }

  // transitionend fires per property and bubbles — act only on this element's transform edge.
  private isSlideEnd(event: TransitionEvent): boolean {
    return event.target === event.currentTarget && event.propertyName === 'transform';
  }

  onNavItemSlideEnd(event: TransitionEvent): void {
    if (this.isSlideEnd(event)) this.focusPreviousElem();
  }

  onSubmenuSlideEnd(event: TransitionEvent): void {
    if (this.isSlideEnd(event)) this.focusSublinkTitle();
  }

  resetOnBlur(target: EventTarget | null) {
    const lastElem = this.subLinkElemItems().at(-1)?.nativeElement;
    if (lastElem && target === lastElem) {
      this.focusSublinkTitle();
    }
  }
}
